import type { IBaseCampaignProduct, ICampaignProduct } from '~/modules/campaign-product';
import type { ReactNode } from 'react';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCGPriceContext } from '~/modules/campaign-group-price';

export interface ICampaignProductPriceContext {
  selectedCampaignProducts?: IBaseCampaignProduct[];
  handleSelectedCampaignProducts: (products?: IBaseCampaignProduct[]) => void;
  handleResetSelectedCampaignProducts: () => void;
}
export interface ICampaignProductPriceProvider {
  children: ReactNode;
  data?: ICampaignProduct;
}

export const CampaignProductPriceContext = createContext<ICampaignProductPriceContext | undefined>(
  undefined,
);

export const CampaignProductPriceProvider: React.FC<ICampaignProductPriceProvider> = ({
  children,
}) => {
  const [selectedCampaignProducts, setSelectedCampaignProducts] =
    useState<IBaseCampaignProduct[]>();
  const { campaignGroupProducts } = useCGPriceContext();

  const handleSelectedCampaignProducts = useCallback((products?: IBaseCampaignProduct[]) => {
    setSelectedCampaignProducts(products);
  }, []);
  const handleResetSelectedCampaignProducts = useCallback(() => {
    if (!campaignGroupProducts?.length) return;
    setSelectedCampaignProducts(campaignGroupProducts.flatMap((c) => c.product || []));
  }, [campaignGroupProducts]);

  useEffect(() => {
    if (
      !campaignGroupProducts?.length ||
      !!selectedCampaignProducts?.length ||
      typeof selectedCampaignProducts !== 'undefined'
    )
      return;

    setSelectedCampaignProducts(campaignGroupProducts.flatMap((c) => c.product || []));
  }, [campaignGroupProducts, selectedCampaignProducts]);

  const contextValue = useMemo(
    () => ({
      selectedCampaignProducts,
      handleSelectedCampaignProducts,
      handleResetSelectedCampaignProducts,
    }),
    [handleResetSelectedCampaignProducts, handleSelectedCampaignProducts, selectedCampaignProducts],
  );

  return (
    <CampaignProductPriceContext.Provider value={contextValue}>
      {children}
    </CampaignProductPriceContext.Provider>
  );
};

export const useCampaignProductPriceContext = () => {
  const context = useContext(CampaignProductPriceContext);
  if (!context) {
    throw new Error(
      'useCampaignProductPriceContext must be used within a CampaignProductPriceProvider',
    );
  }
  return context;
};
